import { useState, useEffect, useCallback, Dispatch, SetStateAction } from "react";
import { useTenant } from "../../Contexts/TenantContext";
import { CreditCardInstallmentOptions } from "../../Models/Payment";
import { CreditCardData } from "../../Types/CreditCardDataType";
import { formatPriceBRL } from "../../Utils/DataFormatters";
import { identifyBrand, listInstallments } from "../../Utils/efiBankPayment";
import { Select } from "../Select/Select";
import { toast } from "react-toastify";
import FloatingInput from "../FloatingInput/FloatingInput";
import EfiPay from "payment-token-efi";
import visaIcon from "../../assets/payments/visa.svg";
import mastercardIcon from "../../assets/payments/mastercard.svg";
import amexIcon from "../../assets/payments/amex.svg";
import eloIcon from "../../assets/payments/elo.svg";

interface PaymentOptionCardProps {
  id: string;
  label: string;
  description: string;
  selected: boolean;
  onSelect: (id: string) => void;
  isEnabled: boolean;
  orderPrice?: number;
  creditCardData?: CreditCardData;
  onCreditCardDataChange?: Dispatch<SetStateAction<CreditCardData>>;
  useHostedCreditCardCheckout?: boolean;
};

const PaymentOptionCard = ({
  id,
  label,
  description,
  selected,
  onSelect,
  isEnabled,
  orderPrice,
  creditCardData,
  onCreditCardDataChange,
  useHostedCreditCardCheckout
}: PaymentOptionCardProps) => {
  const { tenantPreference } = useTenant();
  const [isMobile, setIsMobile] = useState(false);
  const [installments, setInstallments] = useState<CreditCardInstallmentOptions[]>([]);
  const [isFetchingInstallments, setIsFetchingInstallments] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleChange = useCallback(
    (field: keyof CreditCardData, value: string | number | null) => {
      if (!onCreditCardDataChange) {
        return;
      }

      const newValue =
        field === "selectedInstallments" && typeof value === "string"
          ? Number(value)
          : value ?? "";

      onCreditCardDataChange(prev => ({
        ...prev,
        [field]: newValue
      }));
    },
    [onCreditCardDataChange]
  );

  const handleCardNumberChange = useCallback(async (value: string = "") => {
    handleChange("cardNumber", value);

    const cleanNumber = value.replace(/\s/g, "");

    if (!cleanNumber) {
      handleChange("cardBrand", null);
      handleChange("selectedInstallments", 1);
      setInstallments([]);
    }
  }, [handleChange]);

  useEffect(() => {
    if (useHostedCreditCardCheckout) {
      return;
    }

    if (!creditCardData?.cardNumber) {
      return;
    }

    const cleanNumber = creditCardData.cardNumber.replace(/\s/g, "");

    if (cleanNumber.length < 6) {
      setInstallments([]);
      handleChange("cardBrand", null);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        setIsFetchingInstallments(true);

        const detectedBrand = await identifyBrand(cleanNumber);
        if (!detectedBrand) {
          toast.error("Não foi possível identificar a bandeira do cartão.");
          handleChange("cardBrand", null);
          setInstallments([]);
          return;
        }

        handleChange("cardBrand", detectedBrand);

        if (!orderPrice) {
          return;
        }

        const totalCents = Math.round(orderPrice * 100);
        const efiInstallments = await listInstallments(detectedBrand, totalCents);

        if (!efiInstallments) {
          toast.error("Não foi possível buscar as parcelas para esse cartão.");
          setInstallments([]);
        } else {
          const maxNoInterest = tenantPreference?.cardMaxNoInterestInstallments ?? 0;
          const basePrice = orderPrice;

          const mapped: CreditCardInstallmentOptions[] = efiInstallments.installments.map((i: EfiPay.CreditCard.Installment) => {
            const installments = i.installment;
            // EFI returns `value` as the per-installment amount in cents
            const gatewayPerInstallment = i.value / 100;
            const gatewayTotalCharge = gatewayPerInstallment * installments;
            const storeAbsorbs = installments <= maxNoInterest;

            const installmentPrice = storeAbsorbs ? basePrice / installments : gatewayPerInstallment;
            const totalPrice = storeAbsorbs ? basePrice : gatewayTotalCharge;
            const feeAmount = storeAbsorbs ? 0 : Math.max(0, gatewayTotalCharge - basePrice);
            const feePercentApplied = basePrice > 0 ? feeAmount / basePrice : 0;

            return {
              installments,
              totalPrice,
              installmentPrice,
              monthlyInterestRate: storeAbsorbs ? 0 : i.interest_percentage / 100,
              totalInterest: storeAbsorbs ? 0 : feeAmount,
              feePercentApplied,
              feeAmount,
              feePaidBy: storeAbsorbs ? "Store" as const : "Customer" as const
            };
          });
          setInstallments(mapped);
        }
      } catch {
        toast.error("Erro ao buscar parcelas. Tente novamente.");
        setInstallments([]);
      } finally {
        setIsFetchingInstallments(false);
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [creditCardData?.cardNumber, orderPrice, tenantPreference?.cardMaxNoInterestInstallments, handleChange, useHostedCreditCardCheckout]);

  const formatDocument = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 14);
    if (digits.length <= 11) {
      return digits
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    }
    return digits
      .replace(/(\d{2})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
  };

  const handleClick = () => {
    if (!isEnabled) {
      toast.error("Essa forma de pagamento não foi permitida pela empresa para o seu pedido.");
      return;
    }

    onSelect(id);
  };

  return (
    <div
      className={`border rounded p-4 cursor-pointer transition-all ${
        selected ? "border-primary-600 bg-primary-50" : "border-gray-300"
      } ${!isEnabled ? "opacity-50 cursor-not-allowed" : !selected ? "hover:border-primary-600" : ""}`}
      onClick={handleClick}
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition ${
            selected ? "border-primary-600" : "border-gray-400"
          }`}
        >
          {selected && (
            <div className="w-3 h-3 bg-primary-600 rounded-full"></div>
          )}
        </div>
        <span className="font-semibold">
          {label}
        </span>
      </div>

      {selected && (
        <div className="mt-3 text-sm text-gray-600">
          {id === "credit-card" && isEnabled && !useHostedCreditCardCheckout ? (
            <div className="space-y-2">
              <div className="flex justify-center items-center gap-6 h-10">
                {!creditCardData?.cardNumber && (
                  <>
                    <img src={visaIcon} alt="Visa" className="h-[26px]" />
                    <img src={mastercardIcon} alt="Mastercard" className="h-[26px]" />
                    <img src={amexIcon} alt="Amex" className="h-[26px]" />
                    <img src={eloIcon} alt="Elo" className="h-[26px]" />
                  </>
                )}

                {creditCardData?.cardNumber && creditCardData.cardBrand && (
                  <>
                    {creditCardData.cardBrand === "visa" && <img src={visaIcon} alt="Visa" className="h-[26px]" />}
                    {creditCardData.cardBrand === "mastercard" && <img src={mastercardIcon} alt="Mastercard" className="h-[26px]" />}
                    {creditCardData.cardBrand === "amex" && <img src={amexIcon} alt="Amex" className="h-[26px]" />}
                    {creditCardData.cardBrand === "elo" && <img src={eloIcon} alt="Elo" className="h-[26px]" />}
                  </>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <div className="basis-full md:basis-[calc(50%-4px)] md:flex-1">
                  <FloatingInput
                    label={"Número do Cartão"}
                    name="cardNumber"
                    value={creditCardData?.cardNumber ?? ""}
                    onChange={e => handleCardNumberChange(e.target.value ?? "")}
                    required
                    mask="9999 9999 9999 9999"
                    autoComplete="cc-number"
                  />
                </div>

                <div className="basis-full md:basis-[calc(50%-4px)] md:flex-1">
                  <FloatingInput
                    label={"Nome impresso no cartão"}
                    name="cardName"
                    value={creditCardData?.cardName}
                    onChange={e => handleChange("cardName", e.target.value.replace(/\d/g, "").toUpperCase())}
                    required
                    autoComplete="cc-name"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <div className="basis-[calc(50%-4px)] flex-1 md:basis-0">
                  <FloatingInput
                    label="Validade (MM/AA)"
                    name="cardExpiration"
                    value={creditCardData?.cardExpiration}
                    onChange={e => handleChange("cardExpiration", e.target.value)}
                    required
                    mask="99/99"
                    autoComplete="cc-exp"
                  />
                </div>

                <div className="basis-[calc(50%-4px)] flex-1 md:basis-0">
                  <FloatingInput
                    label="CVV"
                    name="cardCvv"
                    value={creditCardData?.cardCvv}
                    onChange={e => handleChange("cardCvv", e.target.value)}
                    required
                    mask="999"
                    autoComplete="cc-csc"
                  />
                </div>

                <div className="basis-full flex-1 md:basis-0">
                  <FloatingInput
                    label={isMobile ? "Doc. do titular" : "Documento do titular (CPF/CNPJ)"}
                    name="holderDocument"
                    value={creditCardData?.holderDocument}
                    onChange={e => handleChange("holderDocument", formatDocument(e.target.value))}
                    required
                  />
                </div>
              </div>

              {isFetchingInstallments ? (
                <p className="text-xs md:text-sm">{"Carregando opções de parcelamento..."}</p>
              ) : (
                (() => {
                  const cardDigits = creditCardData?.cardNumber?.replace(/\D/g, "") ?? "";
                  if (cardDigits.length < 6 || installments.length === 0) {
                    return (
                      <p className="text-xs md:text-sm text-gray-500">
                        {"Informe o número do cartão para exibir as parcelas."}
                      </p>
                    );
                  }

                  const MAX_MONTHLY_RATE = 0.5;
                  const validInstallments = installments.filter(
                    i => i.monthlyInterestRate <= MAX_MONTHLY_RATE
                  );

                  if (validInstallments.length === 0) {
                    return (
                      <p className="text-xs md:text-sm text-gray-500">
                        {"Não há opções de parcelamento disponíveis para este cartão/valor."}
                      </p>
                    );
                  }

                  return (
                    <Select
                      label={"Parcelas"}
                      name="selectedInstallments"
                      value={creditCardData?.selectedInstallments ?? 1}
                      onChange={e => handleChange("selectedInstallments", e.target.value)}
                      required
                      options={validInstallments.map(i => ({
                        value: i.installments,
                        label:
                          i.monthlyInterestRate <= 0 || i.installments === 1
                            ? `${i.installments}x ${"de"} ${formatPriceBRL(i.installmentPrice)} (${"sem juros"})`
                            : `${i.installments}x ${"de"} ${formatPriceBRL(i.installmentPrice)} (${"com juros"} ${(i.monthlyInterestRate * 100).toFixed(2)}% ${"ao mês"})`
                      }))}
                    />
                  );
                })()
              )}
            </div>
          ) : (
            <p>{description}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentOptionCard;
